import { Link } from 'react-router-dom'
import { HiArrowLeft } from 'react-icons/hi'
import './legal.css'

export default function PrivacyPage() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-back">
          <HiArrowLeft />
          Back to Home
        </Link>
      </nav>

      <article className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            StatScout ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
            explains how we collect, use, and safeguard your information when you use our Service.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>

          <h3>2.1 Information You Provide</h3>
          <ul>
            <li><strong>Account Information:</strong> Email address, name (optional), and password when you create an account</li>
            <li><strong>Payment Information:</strong> Billing details processed securely through Stripe (we do not store your full card number)</li>
            <li><strong>User Preferences:</strong> Favourite teams, leagues, and notification settings</li>
            <li><strong>Tracked Bets:</strong> Any bets you choose to track within the platform</li>
          </ul>

          <h3>2.2 Information Collected Automatically</h3>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features used, and time spent on the Service</li>
            <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
            <li><strong>Log Data:</strong> IP address, access times, and referring URLs</li>
            <li><strong>Cookies:</strong> Session and preference cookies (see Section 6)</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send important updates about your account or the Service</li>
            <li>Personalize your experience (e.g., favourite leagues)</li>
            <li>Improve our Service through analytics</li>
            <li>Respond to support requests</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>
          <p>
            <strong>We do not sell your personal information to third parties.</strong>
          </p>
        </section>

        <section>
          <h2>4. Data Sharing</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Payment Processors:</strong> Stripe, for secure payment processing</li>
            <li><strong>Authentication Services:</strong> Clerk, for secure account management</li>
            <li><strong>Analytics Providers:</strong> To help us understand Service usage (anonymized where possible)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your data, including:
          </p>
          <ul>
            <li>HTTPS encryption for all data in transit</li>
            <li>Secure password hashing</li>
            <li>Regular security audits</li>
            <li>Limited employee access to personal data</li>
          </ul>
          <p>
            However, no method of transmission over the Internet is 100% secure. We cannot guarantee
            absolute security.
          </p>
        </section>

        <section>
          <h2>6. Cookies</h2>
          <p>We use cookies for:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, preferences)</li>
            <li><strong>Analytics Cookies:</strong> To understand how you use our Service</li>
          </ul>
          <p>
            You can control cookies through your browser settings, but disabling essential cookies may
            affect Service functionality.
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>We retain your data for as long as your account is active or as needed to provide the Service. After account deletion:</p>
          <ul>
            <li>Account data is deleted within 30 days</li>
            <li>Payment records are retained as required by law (typically 7 years)</li>
            <li>Anonymized analytics data may be retained indefinitely</li>
          </ul>
        </section>

        <section>
          <h2>8. Your Rights (GDPR)</h2>
          <p>If you are in the UK or EU, you have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate data</li>
            <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain processing of your data</li>
            <li><strong>Withdraw Consent:</strong> Where processing is based on consent</li>
          </ul>
          <p>
            To exercise these rights, contact us at <a href="mailto:privacy@statscout.io">privacy@statscout.io</a>.
          </p>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            Our Service is not intended for anyone under 18 years of age. We do not knowingly collect
            personal information from children under 18. If we become aware that we have collected such
            information, we will delete it immediately.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your data may be processed in countries outside your own. We ensure appropriate safeguards
            are in place for such transfers, including Standard Contractual Clauses where applicable.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Links</h2>
          <p>
            Our Service may contain links to third-party websites (e.g., bookmakers, responsible gambling
            resources). We are not responsible for the privacy practices of these external sites.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant changes
            via email or through the Service. The "Last updated" date at the top indicates when this policy
            was last revised.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>For questions or concerns about this Privacy Policy, contact us at:</p>
          <p>
            <a href="mailto:privacy@statscout.io">privacy@statscout.io</a>
          </p>
          <p>
            Data Controller: StatScout<br />
            United Kingdom
          </p>
        </section>
      </article>

      <footer className="legal-footer">
        <p>© {new Date().getFullYear()} StatScout. All rights reserved.</p>
      </footer>
    </div>
  )
}
